import * as React from "react";
import type { IconName } from "../../icons";
import { icons } from "../../icons";
import {
  ButtonOrLink,
  type ButtonOrLinkProps,
  type DistributiveOmit,
} from "../ButtonOrLink/ButtonOrLink";
import styles from "./IconButton.css";

const sizeMapIcon = {
  small: "16px",
  medium: "20px",
  large: "24px",
} as const;

type Size = keyof typeof sizeMapIcon;
type Color = "neutral" | "accent" | "success" | "error";
type Variant = "primary" | "secondary" | "tertiary";

// Renders a `<button>` by default, react-router's `<Link>` when given `to` and
// an `<a>` when given `href` — see `ButtonOrLink` for how the shapes exclude
// each other.
//
// Everything the rendered element already gives us for free (onClick,
// disabled, id, aria-current, onPointerDown, ...) flows through as-is via
// `...rest` — that's also what lets a wrapper like Radix's `asChild` inject its
// own handlers and ARIA attributes and have them actually reach the DOM node.
// `className` is excluded because the recipe below is this component's whole
// job, not the caller's; `children` because the icon is. `aria-label` is
// excluded in favor of `ariaLabel`, so there's exactly one way to set it.
export type IconButtonProps = DistributiveOmit<
  ButtonOrLinkProps,
  "className" | "children" | "aria-label"
> & {
  icon: IconName;
  ariaLabel: string;
  size?: Size;
  color?: Color;
  variant?: Variant;
};

const IconButton = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  IconButtonProps
>(
  (
    {
      icon,
      ariaLabel,
      size = "medium",
      color = "neutral",
      variant = "primary",
      ...rest
    },
    ref,
  ) => {
    const Icon = icons[icon];

    return (
      <ButtonOrLink
        {...rest}
        ref={ref}
        aria-label={ariaLabel}
        className={styles.iconButton({ size, color, variant })}
      >
        <Icon size={sizeMapIcon[size]} />
      </ButtonOrLink>
    );
  },
);

export default IconButton;
